import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import { useVehicles } from '../context/VehiclesContext';
import { formatEngineSizeForDisplay } from '../utils/engineSize';

function buildBudgetOptions(vehicles, priceType) {
  const step = priceType === 'sale' ? 1000 : 50;
  const maxValue = vehicles.reduce((highest, vehicle) => {
    const vehicleValue = Number(
      priceType === 'sale' ? vehicle.salePrice : vehicle.financePrice
    ) || 0;

    return Math.max(highest, vehicleValue);
  }, 0);

  const roundedMax = Math.max(step, Math.ceil(maxValue / step) * step);
  const options = [50];
  const startingValue = step === 50 ? 100 : step;

  for (let value = startingValue; value <= roundedMax; value += step) {
    options.push(value);
  }

  return options;
}

function formatCurrency(value) {
  return `£${Number(value).toLocaleString()}`;
}

function getTimestampValue(timestamp) {
  if (!timestamp) {
    return 0;
  }

  if (typeof timestamp === 'number') {
    return timestamp;
  }

  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof timestamp === 'object') {
    if (typeof timestamp.seconds === 'number') {
      return timestamp.seconds * 1000;
    }

    if (typeof timestamp._seconds === 'number') {
      return timestamp._seconds * 1000;
    }

    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
  }

  return 0;
}

const sortOptions = [
  { value: 'latest', label: 'Latest Arrivals' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'make-asc', label: 'Make A-Z' },
  { value: 'model-asc', label: 'Model A-Z' },
];

const validSortOptions = new Set(sortOptions.map((option) => option.value));
const validVehicleTypes = new Set(['car', 'van', 'pickup', 'motorhome', 'other']);

function normalizeVehicleType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return validVehicleTypes.has(normalized) ? normalized : 'car';
}

function SearchResultsPage({ authUser }) {
  const pageSize = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const { vehicles, loading, error } = useVehicles();
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 950px)');

    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);

      if (!event.matches) {
        setIsMobileFiltersOpen(true);
      } else {
        setIsMobileFiltersOpen(false);
      }
    };

    handleViewportChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  const filters = useMemo(() => {
    const requestedSortBy = searchParams.get('sortBy') || 'latest';
    const requestedVehicleType = String(searchParams.get('type') || '').trim().toLowerCase();

    return {
      vehicleType: validVehicleTypes.has(requestedVehicleType) ? requestedVehicleType : '',
      make: searchParams.get('make') || '',
      model: searchParams.get('model') || '',
      priceType: searchParams.get('priceType') === 'finance' ? 'finance' : 'sale',
      minBudget: searchParams.get('minBudget') || '',
      maxBudget: searchParams.get('maxBudget') || '',
      sortBy: validSortOptions.has(requestedSortBy) ? requestedSortBy : 'latest',
    };
  }, [searchParams]);

  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const sourceVehicles = useMemo(() => {
    const unsoldVehicles = vehicles.filter((vehicle) => !vehicle.isSold);
    return unsoldVehicles.length > 0 ? unsoldVehicles : vehicles;
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    return sourceVehicles
      .filter((vehicle) => {
        if (filters.vehicleType && normalizeVehicleType(vehicle.vehicleType) !== filters.vehicleType) {
          return false;
        }

        if (filters.make && vehicle.make !== filters.make) {
          return false;
        }

        if (filters.model && vehicle.model !== filters.model) {
          return false;
        }

        const comparisonValue = Number(
          filters.priceType === 'sale' ? vehicle.salePrice : vehicle.financePrice
        ) || 0;

        if (filters.minBudget && comparisonValue < Number(filters.minBudget)) {
          return false;
        }

        if (filters.maxBudget && comparisonValue > Number(filters.maxBudget)) {
          return false;
        }

        return true;
      })
      .sort((left, right) => {
        if (filters.sortBy === 'latest') {
          return getTimestampValue(right.createdAt) - getTimestampValue(left.createdAt);
        }

        if (filters.sortBy === 'price-desc' || filters.sortBy === 'price-asc') {
          const leftValue = Number(
            filters.priceType === 'sale' ? left.salePrice : left.financePrice
          ) || 0;
          const rightValue = Number(
            filters.priceType === 'sale' ? right.salePrice : right.financePrice
          ) || 0;

          return filters.sortBy === 'price-desc'
            ? rightValue - leftValue
            : leftValue - rightValue;
        }

        if (filters.sortBy === 'make-asc') {
          const makeCompare = (left.make || '').localeCompare(right.make || '');
          if (makeCompare !== 0) {
            return makeCompare;
          }

          return (left.model || '').localeCompare(right.model || '');
        }

        return (left.model || '').localeCompare(right.model || '')
          || (left.make || '').localeCompare(right.make || '');
      });
  }, [filters.make, filters.maxBudget, filters.minBudget, filters.model, filters.priceType, filters.sortBy, filters.vehicleType, sourceVehicles]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / pageSize));

  const paginatedVehicles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredVehicles.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredVehicles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.make, filters.maxBudget, filters.minBudget, filters.model, filters.priceType, filters.sortBy, filters.vehicleType]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const vehicleTypeMatchedVehicles = useMemo(() => {
    if (!draftFilters.vehicleType) {
      return sourceVehicles;
    }

    return sourceVehicles.filter(
      (vehicle) => normalizeVehicleType(vehicle.vehicleType) === draftFilters.vehicleType
    );
  }, [draftFilters.vehicleType, sourceVehicles]);

  const availableMakes = useMemo(() => {
    return [...new Set(vehicleTypeMatchedVehicles.map((vehicle) => vehicle.make).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
  }, [vehicleTypeMatchedVehicles]);

  const makeMatchedVehicles = useMemo(() => {
    if (!draftFilters.make) {
      return vehicleTypeMatchedVehicles;
    }

    return vehicleTypeMatchedVehicles.filter((vehicle) => vehicle.make === draftFilters.make);
  }, [draftFilters.make, vehicleTypeMatchedVehicles]);

  const availableModels = useMemo(() => {
    if (!draftFilters.make) {
      return [];
    }

    return [...new Set(makeMatchedVehicles.map((vehicle) => vehicle.model).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
  }, [draftFilters.make, makeMatchedVehicles]);

  const budgetOptions = useMemo(() => {
    return buildBudgetOptions(vehicleTypeMatchedVehicles, draftFilters.priceType);
  }, [draftFilters.priceType, vehicleTypeMatchedVehicles]);

  const minBudgetOptions = useMemo(() => {
    if (!draftFilters.maxBudget) {
      return budgetOptions;
    }

    return budgetOptions.filter((option) => option <= Number(draftFilters.maxBudget));
  }, [budgetOptions, draftFilters.maxBudget]);

  const maxBudgetOptions = useMemo(() => {
    if (!draftFilters.minBudget) {
      return budgetOptions;
    }

    return budgetOptions.filter((option) => option >= Number(draftFilters.minBudget));
  }, [budgetOptions, draftFilters.minBudget]);

  const handleDraftFilterChange = (event) => {
    const { name, value } = event.target;

    setDraftFilters((current) => {
      const next = { ...current, [name]: value };

      if (name === 'make' && value !== current.make) {
        next.model = '';
      }

      if (name === 'vehicleType' && value !== current.vehicleType) {
        next.make = '';
        next.model = '';
      }

      if (name === 'minBudget' && current.maxBudget && Number(value) > Number(current.maxBudget)) {
        next.maxBudget = '';
      }

      if (name === 'maxBudget' && current.minBudget && Number(value) < Number(current.minBudget)) {
        next.minBudget = '';
      }

      return next;
    });
  };

  const handleDraftPriceTypeToggle = () => {
    setDraftFilters((current) => ({
      ...current,
      priceType: current.priceType === 'sale' ? 'finance' : 'sale',
      minBudget: '',
      maxBudget: '',
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    if (draftFilters.vehicleType) {
      params.set('type', draftFilters.vehicleType);
    }

    if (draftFilters.make) {
      params.set('make', draftFilters.make);
    }

    if (draftFilters.model) {
      params.set('model', draftFilters.model);
    }

    params.set('priceType', draftFilters.priceType);

    if (draftFilters.minBudget) {
      params.set('minBudget', draftFilters.minBudget);
    }

    if (draftFilters.maxBudget) {
      params.set('maxBudget', draftFilters.maxBudget);
    }

    if (draftFilters.sortBy && draftFilters.sortBy !== 'latest') {
      params.set('sortBy', draftFilters.sortBy);
    }

    setSearchParams(params);
  };

  const handleSortChange = (event) => {
    const nextSortBy = event.target.value;

    setDraftFilters((current) => ({
      ...current,
      sortBy: nextSortBy,
    }));

    const params = new URLSearchParams(searchParams);

    if (nextSortBy === 'latest') {
      params.delete('sortBy');
    } else {
      params.set('sortBy', nextSortBy);
    }

    setSearchParams(params);
  };

  const clearFilters = () => {
    setDraftFilters({
      vehicleType: '',
      make: '',
      model: '',
      priceType: 'sale',
      minBudget: '',
      maxBudget: '',
      sortBy: 'latest',
    });
    setSearchParams({ priceType: 'sale' });
  };

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <main className="content-wrap content-wrap-single">
        <section className="content-page-block search-results-shell">
          <div className="section-header">
            <h2>Search Results</h2>
            <Link to="/" className="ghost-btn vehicle-back-link">Back Home</Link>
          </div>

          <div className="search-results-layout">
            <aside className="search-results-sidebar">
              <div className="search-results-sidebar-header">
                <h3 className="search-results-sidebar-title">Refine Search</h3>
                {isMobileViewport && (
                  <button
                    type="button"
                    className="search-results-filter-toggle-btn"
                    onClick={() => setIsMobileFiltersOpen((current) => !current)}
                    aria-expanded={isMobileFiltersOpen}
                    aria-controls="search-results-filters-form"
                  >
                    {isMobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
                  </button>
                )}
              </div>

              {(!isMobileViewport || isMobileFiltersOpen) && (
                <form className="search-panel" onSubmit={applyFilters} id="search-results-filters-form">
                <label className="search-field">
                  <span>Vehicle Type</span>
                  <select
                    name="vehicleType"
                    value={draftFilters.vehicleType}
                    onChange={handleDraftFilterChange}
                  >
                    <option value="">All types</option>
                    <option value="car">Car</option>
                    <option value="van">Van</option>
                    <option value="pickup">Pickup</option>
                    <option value="motorhome">Motorhome</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label className="search-field">
                  <span>Make</span>
                  <select name="make" value={draftFilters.make} onChange={handleDraftFilterChange}>
                    <option value="">All makes</option>
                    {availableMakes.map((make) => (
                      <option key={make} value={make}>
                        {make}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="search-field">
                  <span>Model</span>
                  <select
                    name="model"
                    value={draftFilters.model}
                    onChange={handleDraftFilterChange}
                    disabled={!draftFilters.make}
                  >
                    <option value="">All models</option>
                    {availableModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="toggle-row" role="group" aria-label="Budget mode">
                  <span className={draftFilters.priceType === 'sale' ? 'active' : ''}>Sale</span>
                  <button
                    type="button"
                    className={`switch ${draftFilters.priceType === 'finance' ? 'on' : ''}`}
                    onClick={handleDraftPriceTypeToggle}
                    aria-label="Toggle between sale and finance"
                  >
                    <span />
                  </button>
                  <span className={draftFilters.priceType === 'finance' ? 'active' : ''}>
                    Finance
                  </span>
                </div>

                <label className="search-field">
                  <span>Minimum Budget</span>
                  <select name="minBudget" value={draftFilters.minBudget} onChange={handleDraftFilterChange}>
                    <option value="">No minimum</option>
                    {minBudgetOptions.map((option) => (
                      <option key={`min-${option}`} value={option}>
                        {formatCurrency(option)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="search-field">
                  <span>Maximum Budget</span>
                  <select name="maxBudget" value={draftFilters.maxBudget} onChange={handleDraftFilterChange}>
                    <option value="">No maximum</option>
                    {maxBudgetOptions.map((option) => (
                      <option key={`max-${option}`} value={option}>
                        {formatCurrency(option)}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="search-actions">
                  <button type="submit" className="search-submit-btn">
                    Apply Filters
                  </button>
                  <button type="button" className="ghost-btn" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
                </form>
              )}
            </aside>

            <div className="search-results-main">
              <div className="search-results-toolbar">
                {!loading && !error && (
                  <p className="status-text">
                    {filteredVehicles.length === 0
                      ? 'No vehicles match your selected filters.'
                      : `${filteredVehicles.length} vehicle${filteredVehicles.length === 1 ? '' : 's'} found`}
                  </p>
                )}

                <label className="search-results-sort">
                  <span>Sort by</span>
                  <select
                    name="sortBy"
                    value={draftFilters.sortBy}
                    onChange={handleSortChange}
                    aria-label="Sort search results"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {loading && <p className="status-text">Loading search results...</p>}

              {error && <p className="status-text">{error}</p>}

              {!loading && !error && filteredVehicles.length > 0 && (
                <>
                  <div className="card-grid">
                    {paginatedVehicles.map((vehicle) => (
                      <Link
                        className="vehicle-card-link"
                        key={vehicle.id}
                        to={`/vehicle/${vehicle.id}`}
                        aria-label={`View details for ${vehicle.make} ${vehicle.model}`}
                      >
                        <article className="vehicle-card">
                          <div className="vehicle-image-wrap">
                            <img
                              src={vehicle.imageUrls?.[0] || vehicle.imageUrl}
                              alt={`${vehicle.make} ${vehicle.model}`}
                            />
                          </div>
                          <div className="vehicle-card-body">
                            <h3>
                              {vehicle.make} {vehicle.model}
                            </h3>
                            <p>
                              {vehicle.year} | {Number(vehicle.mileage).toLocaleString()} miles
                            </p>
                            <p>
                              {vehicle.fuelType || 'n/a'} | {vehicle.gearboxType || 'n/a'} | {formatEngineSizeForDisplay(vehicle.engineSize)}
                            </p>
                            <div className="prices">
                              <strong>£{Number(vehicle.salePrice).toLocaleString()}</strong>
                              <span>or £{Number(vehicle.financePrice).toLocaleString()}/month</span>
                            </div>
                          </div>
                        </article>
                      </Link>
                    ))}
                  </div>

                  <nav className="search-results-pagination" aria-label="Search results pages">
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>

                    <div className="search-results-pagination-pages">
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          type="button"
                          className={`search-results-page-btn ${page === currentPage ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                          aria-current={page === currentPage ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </nav>
                </>
              )}

              <div className="search-results-finance-note" aria-label="Representative finance example">
                <p>
                  <strong>Representative Example for Hire Purchase (HP):</strong> Cash price £4,495.00, Annual Interest Rate (fixed) 6.81% p.a., with a representative 12.9% APR, total amount of credit £4,046.00, deposit of £449.00, an initial payment of £90.40, followed by 58 monthly payments of £90.40 with a final payment of £90.40, total amount payable is £5,873.00.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SearchResultsPage;